import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown }     from 'primereact/dropdown'
import { Slider }       from 'primereact/slider'
import { OverlayPanel } from 'primereact/overlaypanel'
import FilterWrapper    from '../../dataCards/dataCardsFiltering/FilterWrapper'
import type { FiltersResponse, ActiveFilters } from '../../../types/Stats'
import { filtersEqual } from '../../../types/Stats'
import { UnitSelect, CompareGeoSelect, ApplyButton } from './FilterParts'
import './Filter.css'
import { useLocalizationLabels } from '../../../context/LocalizationLabelContext'

interface Props {
  filtersResp:         FiltersResponse
  pendingFilters:      ActiveFilters
  appliedFilters:      ActiveFilters
  availableCompareGeo: string[]
  onPendingChange:     (f: ActiveFilters) => void
  onCompareGeoChange:  (list: string[]) => void
  onApply:             () => void
}

function StatsFilter({
  filtersResp,
  pendingFilters, appliedFilters,
  availableCompareGeo,
  onPendingChange, onCompareGeoChange, onApply,
}: Props) {
  const { t } = useTranslation('mainStats')
  const { statsLabels } = useLocalizationLabels()
  const op = useRef<OverlayPanel>(null)
  const detail    = filtersResp.details[pendingFilters.source]
  const dims      = detail?.dims  ?? {}
  const range     = detail?.range ?? { min: 2000, max: new Date().getFullYear() }
  const extraDims = Object.entries(dims).filter(([k]) => k !== 'unit')
  const isDirty = !filtersEqual(pendingFilters, appliedFilters)
  const isServerDirty = !filtersEqual(
    {
      ...pendingFilters,
      yearRange:  appliedFilters.yearRange,
      compareGeo: appliedFilters.compareGeo,
    },
    appliedFilters,
  )

  const update = (key: string, value: any) =>
    onPendingChange({ ...pendingFilters, [key]: value })

  return (
    <FilterWrapper filterComponent={
      <div className="filter-panel">
        <UnitSelect
          unitOptions={dims.unit ?? []}
          value={pendingFilters.unit}
          onChange={v => update('unit', v)}
        />
        {extraDims.map(([dim, values]) => (
          <span key={dim} className="p-float-label">
            <Dropdown
              inputId={`dim-${dim}`}
              value={pendingFilters[dim] ?? (values as string[])[0]}
              options={(values as string[]).map(v => ({
                  label: statsLabels.dim_values?.[v] ?? v,
                  value: v,
              }))}
              onChange={e => update(dim, e.value)}
            />
            <label htmlFor={`dim-${dim}`}>{statsLabels.dims?.[dim] ?? dim}</label>
          </span>
        ))}
        <span className="p-float-label">
          <span className="p-inputwrapper-filled">
            <div
              className="p-inputtext p-component range-input"
              onClick={e => op.current?.toggle(e)}
            >
              {pendingFilters.yearRange[0]} – {pendingFilters.yearRange[1]}
            </div>
          </span>
          <label>{t('filter.years')}</label>
          <OverlayPanel ref={op} dismissable>
            <div className="range-overlay">
              <div className="range-overlay-labels">
                <span>{pendingFilters.yearRange[0]}</span>
                <span>{pendingFilters.yearRange[1]}</span>
              </div>
              <Slider
                value={pendingFilters.yearRange}
                onChange={e => update('yearRange', e.value as [number, number])}
                min={range.min}
                max={range.max}
                range
              />
            </div>
          </OverlayPanel>
        </span>
        <CompareGeoSelect
          compareGeoList={availableCompareGeo}
          value={pendingFilters.compareGeo}
          onChange={onCompareGeoChange}
          disabled={isServerDirty}
        />
        <ApplyButton isDirty={isDirty} onApply={onApply} />
      </div>
    } />
  )
}

export { StatsFilter }
